// =============================================================================
// CLIENT ENTRY POINT
// =============================================================================
// This is the JavaScript entry point that the browser loads.
// It initializes React and mounts the application into the DOM.
//
// This file does three things:
// 1. Imports the base stylesheet and style pack CSS
// 2. Initializes i18n (internationalization) with translations
// 3. Renders the React app into the <div id="app"> element
// =============================================================================

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// --- Stylesheets ---
// ORDER MATTERS: base.css defines the defaults, then the style pack overrides
import '@webzite/core/styles/base.css';
import '@webzite/style-packs/classic-brasserie/tokens.css';

// --- Component styles ---
import '@webzite/core/components/Navbar.css';
import '@webzite/core/components/Hero.css';
import '@webzite/core/components/MenuSection.css';
import '@webzite/core/components/Footer.css';
import '@webzite/core/components/CookieConsent.css';
import '@webzite/core/components/LegalModal.css';
import '@webzite/core/components/LanguageSwitcher.css';
import '@webzite/core/components/DeliveryButtons.css';

// --- i18n initialization ---
import { initI18n } from '@webzite/core/i18n/setup';
import enUI from '@webzite/core/i18n/locales/en/ui.json';
import frUI from '@webzite/core/i18n/locales/fr/ui.json';
import zhUI from '@webzite/core/i18n/locales/zh/ui.json';
import deUI from '@webzite/core/i18n/locales/de/ui.json';
import itUI from '@webzite/core/i18n/locales/it/ui.json';
import ptUI from '@webzite/core/i18n/locales/pt/ui.json';
import enContent from '../content/i18n/en/content.json';
import frContent from '../content/i18n/fr/content.json';
import zhContent from '../content/i18n/zh/content.json';
import deContent from '../content/i18n/de/content.json';
import itContent from '../content/i18n/it/content.json';
import ptContent from '../content/i18n/pt/content.json';
import siteConfig from '../site.config.js';

// --- App ---
import { App } from './App.js';

// Initialize i18n BEFORE rendering the app
initI18n(
  siteConfig.defaultLocale,
  siteConfig.locales,
  { en: enUI, fr: frUI, zh: zhUI, de: deUI, it: itUI, pt: ptUI },
  { en: enContent, fr: frContent, zh: zhContent, de: deContent, it: itContent, pt: ptContent },
);

// Set the theme data attribute on <html> for CSS custom property overrides
document.documentElement.setAttribute('data-theme', siteConfig.stylePack);

// Mount the React app
const container = document.getElementById('app');
if (!container) throw new Error('Root element #app not found');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

// Developer DevTools signature
if (typeof window !== 'undefined' && siteConfig.developer) {
  const devName = siteConfig.developer.name;
  const devUrl = siteConfig.developer.url;
  const devRole = siteConfig.developer.role;
  console.log(
    `%c 🍷 Built with WebZite %c Designed & Developed by ${devName} (${devRole}) %c ${devUrl} `,
    'background: #72222e; color: #ffffff; font-weight: bold; padding: 4px 8px; border-radius: 4px 0 0 4px;',
    'background: #241c16; color: #faf7f2; padding: 4px 8px;',
    'background: #1f1a17; color: #b38f46; font-weight: bold; padding: 4px 8px; border-radius: 0 4px 4px 0;',
  );
}
